<template>
    <Head title="Create Account" />

    <div class="space-y-6">
        <!-- Header -->
        <div class="text-center">
            <h2 class="text-3xl font-bold text-secondary-900 dark:text-white">
                Create your account
            </h2>
            <p class="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                Start your journey with us today
            </p>
        </div>

        <!-- Enterprise Mode Notice -->
        <div v-if="!$page.props.deployment?.features?.registrationEnabled" class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Registration Not Available
                    </h3>
                    <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <p>Self-service registration is currently disabled. Please contact your system administrator for assistance.</p>
                    </div>
                    <div class="mt-4">
                        <Link href="/login" class="text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-600 dark:hover:text-yellow-400">
                            Return to login →
                        </Link>
                    </div>
                </div>
            </div>
        </div>

        <!-- Registration Form -->
        <form v-if="$page.props.deployment?.features?.registrationEnabled" @submit.prevent="submit" class="space-y-4">
            <!-- Name Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- First Name -->
                <div>
                    <label for="first_name" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        First Name <span class="text-danger-500">*</span>
                    </label>
                    <input
                        id="first_name"
                        ref="firstNameInput"
                        v-model="form.first_name"
                        type="text"
                        required
                        :class="inputClass(form.errors.first_name)"
                        placeholder="John"
                    />
                    <p v-if="form.errors.first_name" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                        {{ form.errors.first_name }}
                    </p>
                </div>

                <!-- Last Name -->
                <div>
                    <label for="last_name" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Last Name <span class="text-danger-500">*</span>
                    </label>
                    <input
                        id="last_name"
                        v-model="form.last_name"
                        type="text"
                        required
                        :class="inputClass(form.errors.last_name)"
                        placeholder="Doe"
                    />
                    <p v-if="form.errors.last_name" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                        {{ form.errors.last_name }}
                    </p>
                </div>
            </div>

            <!-- Email -->
            <div>
                <label for="email" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Email Address <span class="text-danger-500">*</span>
                </label>
                <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    required
                    :class="inputClass(form.errors.email)"
                    placeholder="john@example.com"
                />
                <p v-if="form.errors.email" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.email }}
                </p>
            </div>

            <!-- Password -->
            <div>
                <label for="password" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Password <span class="text-danger-500">*</span>
                </label>
                <div class="relative">
                    <input
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        required
                        :class="inputClass(form.errors.password)"
                        placeholder="Min. 8 characters"
                        @input="checkPasswordStrength"
                    />
                    <button
                        type="button"
                        @click="showPassword = !showPassword"
                        class="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                    >
                        <svg v-if="!showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                    </button>
                </div>
                <p v-if="form.errors.password" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.password }}
                </p>

                <!-- Password Strength Indicator -->
                <div v-if="form.password" class="mt-2">
                    <div class="flex items-center space-x-2">
                        <div class="flex-1 bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
                            <div
                                :class="[
                                    'h-2 rounded-full transition-all',
                                    passwordStrength.color
                                ]"
                                :style="{ width: passwordStrength.width }"
                            ></div>
                        </div>
                        <span class="text-sm font-medium" :class="passwordStrength.textColor">
                            {{ passwordStrength.label }}
                        </span>
                    </div>
                    <ul class="mt-2 text-xs space-y-1">
                        <li :class="hasMinLength ? 'text-success-600 dark:text-success-400' : 'text-secondary-500'">
                            {{ hasMinLength ? '✓' : '○' }} At least 8 characters
                        </li>
                        <li :class="hasUpperCase ? 'text-success-600 dark:text-success-400' : 'text-secondary-500'">
                            {{ hasUpperCase ? '✓' : '○' }} One uppercase letter
                        </li>
                        <li :class="hasLowerCase ? 'text-success-600 dark:text-success-400' : 'text-secondary-500'">
                            {{ hasLowerCase ? '✓' : '○' }} One lowercase letter
                        </li>
                        <li :class="hasNumber ? 'text-success-600 dark:text-success-400' : 'text-secondary-500'">
                            {{ hasNumber ? '✓' : '○' }} One number
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Password Confirmation -->
            <div>
                <label for="password_confirmation" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Confirm Password <span class="text-danger-500">*</span>
                </label>
                <input
                    id="password_confirmation"
                    v-model="form.password_confirmation"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    :class="inputClass(form.errors.password_confirmation)"
                    placeholder="Re-enter password"
                />
                <p v-if="form.errors.password_confirmation" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.password_confirmation }}
                </p>
                <p v-else-if="form.password_confirmation && form.password !== form.password_confirmation" class="mt-1 text-sm text-danger-600 dark:text-danger-400">
                    Passwords do not match
                </p>
                <p v-else-if="form.password_confirmation && form.password === form.password_confirmation" class="mt-1 text-sm text-success-600 dark:text-success-400">
                    ✓ Passwords match
                </p>
            </div>

            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="form.processing || !isFormValid"
                class="w-full px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <span v-if="form.processing" class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                </span>
                <span v-else>Create Account</span>
            </button>
        </form>

        <!-- Login Link (show in SaaS mode only - in Enterprise mode, the notice above already has a login link) -->
        <div v-if="$page.props.deployment?.features?.registrationEnabled" class="relative">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-300 dark:border-secondary-600"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    Already have an account?
                </span>
            </div>
        </div>

        <div v-if="$page.props.deployment?.features?.registrationEnabled" class="text-center">
            <Link
                href="/login"
                class="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
                Sign in instead
                <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
            </Link>
        </div>

        <!-- Note about company setup -->
        <div class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p class="text-sm text-blue-700 dark:text-blue-300 text-center">
                After verifying your email, you'll be able to set up your company and choose a subscription plan.
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link, router, useForm } from '@inertiajs/vue3';

// Form state
const form = useForm({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
});

// UI state
const showPassword = ref(false);
const firstNameInput = ref(null);

// Password strength indicators
const hasMinLength = computed(() => form.password.length >= 8);
const hasUpperCase = computed(() => /[A-Z]/.test(form.password));
const hasLowerCase = computed(() => /[a-z]/.test(form.password));
const hasNumber = computed(() => /[0-9]/.test(form.password));

const passwordStrength = computed(() => {
    const password = form.password;
    if (!password) return { width: '0%', label: '', color: '', textColor: '' };

    let strength = 0;
    if (hasMinLength.value) strength++;
    if (hasUpperCase.value) strength++;
    if (hasLowerCase.value) strength++;
    if (hasNumber.value) strength++;

    if (strength === 0) return { width: '0%', label: 'Weak', color: 'bg-danger-500', textColor: 'text-danger-600 dark:text-danger-400' };
    if (strength <= 2) return { width: '33%', label: 'Weak', color: 'bg-danger-500', textColor: 'text-danger-600 dark:text-danger-400' };
    if (strength === 3) return { width: '66%', label: 'Fair', color: 'bg-warning-500', textColor: 'text-warning-600 dark:text-warning-400' };
    return { width: '100%', label: 'Strong', color: 'bg-success-500', textColor: 'text-success-600 dark:text-success-400' };
});

// Form validation
const isFormValid = computed(() => {
    return form.first_name &&
           form.last_name &&
           form.email &&
           form.password &&
           form.password_confirmation &&
           form.password === form.password_confirmation &&
           form.password.length >= 8;
});

// Helper methods for styling
const inputClass = (error) => {
    return [
        'block w-full px-3 py-3 border rounded-lg text-secondary-900 dark:text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
        error
            ? 'border-danger-300 dark:border-danger-600 bg-danger-50 dark:bg-danger-900/20'
            : 'border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700'
    ].join(' ');
};

// Password strength checker
const checkPasswordStrength = () => {
    // Password strength is computed automatically
};

// Submit registration
const submit = () => {
    form.post('/register', {
        preserveScroll: true,
        onSuccess: () => {
            router.visit('/email/verify');
        },
    });
};

// Initialize
onMounted(() => {
    if (firstNameInput.value) {
        firstNameInput.value.focus();
    }
});
</script>

<style scoped>
/* Smooth transitions */
input,
button {
    transition: all 0.15s ease-in-out;
}
</style>
